export const HOST_CONFIG_VIEW = `import React, { useState, useEffect } from 'react';

export default function HostConfigView({ pluginApi, pluginComponents, pluginStyles }) {
  // Safely access components with fallbacks
  const { 
    EventHeader, 
    ContentPanel, 
    AppButton, 
    TextInput, 
    TextArea,
    Checkbox, 
    Alert,
    Spinner
  } = pluginComponents || {};
  
  // Safely access styles with fallbacks
  const styles = {
    container: pluginStyles?.layout?.container || 'p-4',
    text: {
      title: pluginStyles?.text?.title || 'text-2xl font-bold text-white mb-4',
      small: pluginStyles?.text?.small || 'text-sm text-white/70',
    }
  };

  // State for plugin configuration
  const [config, setConfig] = useState({
    allowPersonalNotes: true,
    showActivityFeed: true,
    allowGuestsToPost: true,
    moderateBeforePost: false,
    feedTitle: 'Activity Feed',
    personalNotesTitle: 'Personal Notes',
    welcomeMessage: 'Welcome to the event plugin! Use this space to take notes and interact with other attendees.',
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Load plugin configuration
  useEffect(() => {
    async function loadConfig() {
      try {
        setLoading(true);
        
        // Load plugin configuration
        const configData = await pluginApi.getPluginConfig();
        if (configData) {
          setConfig(prevConfig => ({...prevConfig, ...configData, ...configData.pluginConfig}));
        }
      } catch (err) {
        console.error('Error loading plugin configuration:', err);
        setError('Failed to load plugin configuration. Please try refreshing.');
      } finally {
        setLoading(false);
      }
    }
    
    loadConfig();
  }, []);

  // Handle configuration changes
  const handleConfigChange = (field, value) => {
    setConfig({
      ...config,
      [field]: value
    });
  };

  // Save plugin configuration
  const saveConfig = async () => {
    try {
      await pluginApi.savePluginConfig(config);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving plugin configuration:', err);
      setError('Failed to save plugin configuration. Please try again.');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Reset to factory defaults
  const resetToFactoryDefaults = async () => {
    try {
      setIsResetting(true);
      
      const factoryDefaults = {
        allowPersonalNotes: true,
        showActivityFeed: true,
        allowGuestsToPost: true,
        moderateBeforePost: false,
        feedTitle: 'Activity Feed',
        personalNotesTitle: 'Personal Notes',
        welcomeMessage: 'Welcome to the event plugin! Use this space to take notes and interact with other attendees.',
      };
      
      await pluginApi.savePluginConfig(factoryDefaults);
      setConfig(factoryDefaults);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error resetting to factory defaults:', err);
      setError('Failed to reset to factory defaults. Please try again.');
      setTimeout(() => setError(''), 3000);
    } finally {
      setIsResetting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        {Spinner ? <Spinner size="large" /> : <div>Loading...</div>}
      </div>
    );
  }
  
  return (
    <div className={styles.container}>
      {error && Alert && <Alert type="error" onClose={() => setError('')}>{error}</Alert>}
      {saveSuccess && Alert && <Alert type="success" onClose={() => setSaveSuccess(false)}>Your changes have been saved!</Alert>}
      
      {EventHeader ? (
        <EventHeader title="Configure your Open door experiment">
          This is the admin view of the Open door plugin. Here you can configure the settings for this plugin instance.
        </EventHeader>
      ) : (
        <div className="bg-neutral-800/80 rounded-2xl p-5 mb-4 text-white">
          <h2 className="text-2xl font-medium mb-3">Plugin Configuration</h2>
          <div className="text-white/90 text-lg">
            This is the admin view of the Open door plugin. Here you can configure the settings for this plugin instance.
          </div>
        </div>
      )}
      
      {ContentPanel ? (
        <ContentPanel title="Plugin Configuration">
          <p className={styles.text.small + " mb-4"}>
            Configure how this plugin behaves for all users in this event.
          </p>
          
          <div className="space-y-4 mb-4">
            
              <>
                <TextInput
                  label="Experiment Title"
                  value={config.experimentTitle}
                  onChange={(value) => handleConfigChange('experimentTitle', value)}
                  placeholder="Experiment title"
                />
                
                <TextInput
                  label="Description"
                  value={config.experimentDescription}
                  onChange={(value) => handleConfigChange('experimentDescription', value)}
                  placeholder="Experiment Description"
                />
              

              <TextInput
                  label="Price"
                  value={config.experimentPrice}
                  onChange={(value) => handleConfigChange('experimentPrice', value)}
                  placeholder="Experiment Price"
                />
              

              <TextInput
                  label="Discounted price"
                  value={config.experimentDiscountedPrice}
                  onChange={(value) => handleConfigChange('experimentDiscountedPrice', value)}
                  placeholder="Discounted Price"
                />
              </>
          

            <div>
            <TextInput
                  label="Experiment tracking CTA text"
                  value={config.experimentTrackingCTAText}
                  onChange={(value) => handleConfigChange('experimentTrackingCTAText', value)}
                  placeholder="CTA text"
                />
              <TextInput
                  label="Experiment tracking name"
                  value={config.experimentTrackingName}
                  onChange={(value) => handleConfigChange('experimentTrackingName', value)}
                  placeholder="Tracking name"
                />
            </div>
          </div>
          
          <div className="space-y-3">
            {AppButton ? (
              <>
                <AppButton onClick={saveConfig} className="mb-2">
                  Save Configuration
                </AppButton>
                
                <AppButton 
                  onClick={resetToFactoryDefaults} 
                  className="bg-red-500/20 hover:bg-red-500/30 border-red-500/30"
                  disabled={isResetting}
                >
                  {isResetting ? 'Resetting...' : 'Reset to Factory Defaults'}
                </AppButton>
              </>
            ) : (
              <>
                <button
                  onClick={saveConfig}
                  className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium mb-2"
                >
                  Save Configuration
                </button>
                
                <button
                  onClick={resetToFactoryDefaults}
                  disabled={isResetting}
                  className="w-full py-4 px-6 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-xl text-white text-center font-medium"
                >
                  {isResetting ? 'Resetting...' : 'Reset to Factory Defaults'}
                </button>
              </>
            )}
          </div>
        </ContentPanel>
      ) : (
        <div className="bg-neutral-800/80 border border-white/10 rounded-2xl p-5">
          <h2 className="text-xl font-semibold text-white mb-2">Plugin Configuration</h2>
          <p className="text-sm text-white/70 mb-4">Configure how this plugin behaves for all users in this event.</p>
          
          {/* Configuration controls would go here */}
          
          <div className="mt-4 space-y-3">
            <button 
              onClick={saveConfig}
              className="w-full py-4 px-6 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-center font-medium mb-2"
            >
              Save Configuration
            </button>
            
            <button
              onClick={resetToFactoryDefaults}
              disabled={isResetting}
              className="w-full py-4 px-6 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-xl text-white text-center font-medium"
            >
              {isResetting ? 'Resetting...' : 'Reset to Factory Defaults'}
            </button>
          </div>
        </div>
      )}
      
      <div className={styles.text.small + " mt-4"}>
        <p>User Role: Host Config View</p>
      </div>
    </div>
  );
}`
